import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNative } from '../platform';
import { normalizeImageFile } from '../heic';

export interface PickedPhoto {
  /** Data URL (data:image/...;base64,...) suitable for preview or upload. */
  dataUrl: string;
  format: string;
}

/**
 * Capture or pick a photo. On native this uses the device camera/photo library
 * with the OS permission prompts. On web it falls back to a hidden file input so
 * the same call works everywhere (e.g. avatar / trip-photo upload).
 */
export async function pickPhoto(source: 'camera' | 'photos' | 'prompt' = 'prompt'): Promise<PickedPhoto | null> {
  if (!isNative) return pickPhotoWeb();

  const sourceMap = {
    camera: CameraSource.Camera,
    photos: CameraSource.Photos,
    prompt: CameraSource.Prompt, // native action sheet: Camera or Photo Library
  } as const;

  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: true,
    resultType: CameraResultType.DataUrl,
    source: sourceMap[source],
  });

  if (!photo.dataUrl) return null;
  return { dataUrl: photo.dataUrl, format: photo.format };
}

/** Ensure camera + photo permissions, requesting if needed. Native only. */
export async function ensureCameraPermission(): Promise<boolean> {
  if (!isNative) return true;
  const status = await Camera.checkPermissions();
  if (status.camera === 'granted' && status.photos === 'granted') return true;
  const req = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
  return req.camera === 'granted' || req.photos === 'granted';
}

function pickPhotoWeb(): Promise<PickedPhoto | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.heic,.heif';
    input.onchange = async () => {
      const picked = input.files?.[0];
      if (!picked) return resolve(null);
      // iPhone HEIC photos become JPEGs here, so the preview can show them.
      const file = await normalizeImageFile(picked);
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ dataUrl: reader.result as string, format: file.type.split('/')[1] ?? 'jpeg' });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

/** Convert a data URL to a Blob for uploading to Supabase Storage. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
